import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  FormHelperText,
  Collapse,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { useMutation } from "react-query";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PersonIcon from "@mui/icons-material/Person";

// Import services and utilities
import { createUrl, shortCodeExists } from "../services/airtable";
import {
  isValidUrl,
  normalizeUrl,
  generateUniqueCode,
  createFullShortUrl,
} from "../utils/urlShortener";

// Import auth context for PIC auto-fill
import { useAuth } from "../context/AuthContext";

// Simple function to detect if we're in development
const isDevelopmentMode = () => {
  return !window.location.hostname.includes("richs.my");
};

// Function to validate image URL
const isValidImageUrl = (url) => {
  if (!url) return false;
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp)$/i;
  return imageExtensions.test(url);
};

const UrlCreationModal = ({ open, onClose, onUrlCreated }) => {
  // Auth context - for PIC auto-fill
  const { user } = useAuth();

  // State for form inputs
  const [originalUrl, setOriginalUrl] = useState("");
  const [country, setCountry] = useState("");
  const [channel, setChannel] = useState(""); // Renamed from source to channel
  const [sources, setSources] = useState(""); // New field for sources
  const [campaignname, setCampaignname] = useState("");
  const [pic, setPic] = useState("");
  const [shortUrl, setShortUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [showCopied, setShowCopied] = useState(false);
  const [formValid, setFormValid] = useState(false);
  
  // New states for social preview
  const [showSocialPreview, setShowSocialPreview] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [thumbnailPreviewError, setThumbnailPreviewError] = useState(false);

  // Country options - UPDATED: Added Sullivans
  const countryOptions = ["Malaysia", "Singapore", "Cambodia", "Sullivans"];

  // Channel options - UPDATED: Added Seminar, Exhibition, Workshop, Roadshow
  const channelOptions = [
    "FB",
    "IG",
    "TT",
    "YT",
    "WA", // WhatsApp
    "LK",
    "XHS",
    "L8",
    "Shopee",
    "LZD",
    "Eshop",
    "Seminar",
    "Exhibition",
    "Workshop",
    "Roadshow"
  ];

  // Sources options - UPDATED: Added Invitation
  const sourcesOptions = [
    "Recipe",
    "SEO",
    "Artwork",
    "Video",
    "Invitation"
  ];

  // Auto-fill PIC when modal opens based on logged-in user
  useEffect(() => {
    if (open && user && user.displayName) {
      setPic(user.displayName);
    }
  }, [open, user]);

  // Generate short URL when original URL changes
  useEffect(() => {
    const generateShortUrl = async () => {
      if (originalUrl && isValidUrl(normalizeUrl(originalUrl))) {
        try {
          // Generate a unique code
          const shortCode = await generateUniqueCode(shortCodeExists);

          // Create full short URL - using your function that handles domains internally
          const fullShortUrl = createFullShortUrl(shortCode);

          setShortUrl(fullShortUrl);
          setUrlError("");
        } catch (error) {
          console.error("Error generating short URL:", error);
        }
      }
    };

    generateShortUrl();
  }, [originalUrl]);

  // Validate form
  useEffect(() => {
    const isValid = originalUrl && !urlError && country && channel && sources && campaignname && pic;
    setFormValid(isValid);
  }, [originalUrl, urlError, country, channel, sources, campaignname, pic]);

  // Validate thumbnail URL
  useEffect(() => {
    if (thumbnailUrl) {
      setThumbnailPreviewError(!isValidImageUrl(thumbnailUrl));
    } else {
      setThumbnailPreviewError(false);
    }
  }, [thumbnailUrl]);

  // Create URL with UTM parameters
  const getUrlWithUTM = () => {
    if (!originalUrl || !country || !channel || !sources || !campaignname) return "";

    try {
      const normalizedUrl = normalizeUrl(originalUrl);
      const url = new URL(normalizedUrl);

      // Add UTM parameters with updated structure
      url.searchParams.set("utm_source", channel); // Use channel for utm_source
      url.searchParams.set("utm_medium", sources); // Use sources for utm_medium
      url.searchParams.set("utm_campaign", campaignname);
      // Keep utm_country as a custom parameter
      url.searchParams.set("utm_country", country);

      return url.toString();
    } catch (error) {
      console.error("Error creating UTM URL:", error);
      return normalizeUrl(originalUrl);
    }
  };

  // Create URL mutation
  const createUrlMutation = useMutation(createUrl, {
    onSuccess: (data) => {
      onUrlCreated?.(data);
      // Reset form after successful creation
      resetForm();
    },
    onError: (error) => {
      console.error("Error creating URL:", error);
    },
  });

  // Form handlers
  const handleUrlChange = (e) => {
    const url = e.target.value;
    setOriginalUrl(url);

    // Validate URL format
    if (url && !isValidUrl(normalizeUrl(url))) {
      setUrlError("Please enter a valid URL");
    } else {
      setUrlError("");
    }
  };

  const handleSubmit = () => {
    // Final validation before submitting
    if (!originalUrl) {
      setUrlError("Please enter a URL");
      return;
    }

    if (!isValidUrl(normalizeUrl(originalUrl))) {
      setUrlError("Please enter a valid URL");
      return;
    }

    if (!country || !channel || !sources || !campaignname || !pic) {
      return;
    }

    // Extract short code from the full short URL
    const shortCode = shortUrl.split("/").pop();

    // Get full UTM URL
    const fullutmurl = getUrlWithUTM();

    // Prepare data with optional social preview fields
    const urlData = {
      shortCode,
      originalurl: normalizeUrl(originalUrl),
      country,
      channel, // Updated from source to channel
      sources, // New field
      campaignname,
      pic,
      status: "Active",
      fullshorturl: shortUrl,
      fullutmurl,
    };

    // Add optional social preview fields if provided
    if (thumbnailUrl && isValidImageUrl(thumbnailUrl)) {
      urlData.thumbnailUrl = thumbnailUrl;
    }
    if (metaTitle) {
      urlData.metaTitle = metaTitle;
    }
    if (metaDescription) {
      urlData.metaDescription = metaDescription;
    }

    // Create new URL record
    createUrlMutation.mutate(urlData);
  };

  const resetForm = () => {
    setOriginalUrl("");
    setCountry("");
    setChannel("");
    setSources("");
    setCampaignname("");
    setPic("");
    setShortUrl("");
    setUrlError("");
    setShowSocialPreview(false);
    setThumbnailUrl("");
    setMetaTitle("");
    setMetaDescription("");
    setThumbnailPreviewError(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleCopyShortUrl = () => {
    if (shortUrl) {
      navigator.clipboard
        .writeText(shortUrl)
        .then(() => {
          setShowCopied(true);
          setTimeout(() => setShowCopied(false), 2000);
        })
        .catch((err) => console.error("Failed to copy URL:", err));
    }
  };

  const toggleSocialPreview = () => {
    setShowSocialPreview(!showSocialPreview);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Create New Short URL</DialogTitle>

        <DialogContent>
          {/* Original URL input */}
          <TextField
            autoFocus
            margin="dense"
            id="originalUrl"
            label="Original URL"
            type="url"
            fullWidth
            value={originalUrl}
            onChange={handleUrlChange}
            error={!!urlError}
            helperText={urlError}
            disabled={createUrlMutation.isLoading}
            placeholder="https://example.com/very/long/url/that/needs/shortening"
            sx={{ mb: 2 }}
          />

          {/* PIC field - read-only, auto-filled from logged-in user */}
          <TextField
            margin="dense"
            id="pic"
            label="PIC"
            type="text"
            fullWidth
            value={pic}
            disabled
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{ 
              mb: 2,
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#333333",
                fontWeight: 500,
              },
            }}
          />

          {/* Country dropdown */}
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="country-label">Country *</InputLabel>
            <Select
              labelId="country-label"
              id="country"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              label="Country *"
              disabled={createUrlMutation.isLoading}
            >
              {countryOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Channel dropdown (renamed from Source) */}
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="channel-label">Channel *</InputLabel>
            <Select
              labelId="channel-label"
              id="channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              label="Channel *"
              disabled={createUrlMutation.isLoading}
            >
              {channelOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sources dropdown (new) */}
          <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
            <InputLabel id="sources-label">Sources *</InputLabel>
            <Select
              labelId="sources-label"
              id="sources"
              value={sources}
              onChange={(e) => setSources(e.target.value)}
              label="Sources *"
              disabled={createUrlMutation.isLoading}
            >
              {sourcesOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Campaign Name input - moved down */}
          <TextField
            margin="dense"
            id="campaignname"
            label="Campaign Name *"
            type="text"
            fullWidth
            value={campaignname}
            onChange={(e) => setCampaignname(e.target.value)}
            disabled={createUrlMutation.isLoading}
            sx={{ mb: 2 }}
          />

          {/* Social Preview Section */}
          <Box 
            sx={{ 
              mt: 2, 
              mb: 2,
              borderTop: "1px solid #e0e0e0",
              pt: 2
            }}
          >
            <Button
              onClick={toggleSocialPreview}
              startIcon={showSocialPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              endIcon={
                <Tooltip title="Customize how your link appears when shared on social media">
                  <InfoOutlinedIcon fontSize="small" />
                </Tooltip>
              }
              sx={{ 
                color: "text.secondary",
                textTransform: "none",
                fontWeight: "normal",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "primary.main"
                }
              }}
            >
              Customize Social Preview (optional)
            </Button>

            <Collapse in={showSocialPreview}>
              <Box sx={{ mt: 2 }}>
                {/* Thumbnail URL */}
                <TextField
                  margin="dense"
                  id="thumbnailUrl"
                  label="Preview Image URL"
                  type="url"
                  fullWidth
                  value={thumbnailUrl}
                  onChange={(e) => setThumbnailUrl(e.target.value)}
                  disabled={createUrlMutation.isLoading}
                  placeholder="https://wiki.richproductsmy.kitchen/wp-content/uploads/..."
                  helperText="Upload image to wiki.richproductsmy.kitchen first, then paste URL here. Image size must be exactly 1200x630 pixels and UNDER 200KB for optimal display."
                  error={thumbnailPreviewError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ImageOutlinedIcon color={thumbnailPreviewError ? "error" : "action"} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />

                {/* Image Preview */}
                {thumbnailUrl && isValidImageUrl(thumbnailUrl) && (
                  <Box 
                    sx={{ 
                      mb: 2, 
                      p: 1, 
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      backgroundColor: "#fafafa"
                    }}
                  >
                    <img 
                      src={thumbnailUrl} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: "100%", 
                        maxHeight: "150px", 
                        objectFit: "contain",
                        display: "block",
                        margin: "0 auto"
                      }} 
                      onError={() => setThumbnailPreviewError(true)}
                      onLoad={() => setThumbnailPreviewError(false)}
                    />
                  </Box>
                )}

                {/* Meta Title */}
                <TextField
                  margin="dense"
                  id="metaTitle"
                  label="Preview Title"
                  type="text"
                  fullWidth
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  disabled={createUrlMutation.isLoading}
                  placeholder={campaignname || "Rich Products Malaysia"}
                  helperText={
                    <Box display="flex" justifyContent="space-between">
                      <span>Uses campaign name if empty</span>
                      <span style={{ color: metaTitle.length > 60 ? "#ff9800" : "inherit" }}>
                        {metaTitle.length}/60
                      </span>
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />

                {/* Meta Description */}
                <TextField
                  margin="dense"
                  id="metaDescription"
                  label="Preview Description"
                  type="text"
                  fullWidth
                  multiline
                  rows={2}
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  disabled={createUrlMutation.isLoading}
                  placeholder="Discover delicious recipes and premium baking solutions from Rich Products Malaysia"
                  helperText={
                    <Box display="flex" justifyContent="space-between">
                      <span>Uses default if empty</span>
                      <span style={{ color: metaDescription.length > 160 ? "#ff9800" : "inherit" }}>
                        {metaDescription.length}/160
                      </span>
                    </Box>
                  }
                  sx={{ mb: 2 }}
                />
              </Box>
            </Collapse>
          </Box>

          {/* URL with UTM preview */}
          {originalUrl && !urlError && country && channel && sources && campaignname && (
            <Paper
              sx={{
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "8px",
                margin: "16px 0",
              }}
              elevation={0}
            >
              <Typography variant="body2" color="textSecondary">
                URL with UTM Parameters:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  wordBreak: "break-all",
                  mt: 1,
                  fontSize: "13px",
                }}
              >
                {getUrlWithUTM()}
              </Typography>
            </Paper>
          )}

          {/* Short URL preview */}
          {shortUrl && !urlError && (
            <Paper
              sx={{
                backgroundColor: "#f5f5f5",
                padding: "16px",
                borderRadius: "8px",
                margin: "16px 0",
              }}
              elevation={0}
            >
              <Typography variant="body2" color="textSecondary">
                Short URL Preview:
              </Typography>
              <Box display="flex" alignItems="center" mt={1}>
                <Typography variant="body1" fontWeight="500" mr={2}>
                  {shortUrl}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleCopyShortUrl}
                  disabled={createUrlMutation.isLoading}
                >
                  Copy
                </Button>
              </Box>
              {isDevelopmentMode() && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  Note: In production, this will use the link.richs.my domain.
                </Typography>
              )}
            </Paper>
          )}

          {/* Form validation message */}
          {!formValid && originalUrl && !urlError && (
            <FormHelperText sx={{ color: "text.secondary", mt: 1 }}>
              Please fill in all required fields to create a short URL
            </FormHelperText>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={createUrlMutation.isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formValid || createUrlMutation.isLoading}
          >
            {createUrlMutation.isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Creating...
              </>
            ) : (
              "Create Short URL"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error notification */}
      <Snackbar
        open={createUrlMutation.isSuccess || createUrlMutation.isError}
        autoHideDuration={3000}
        onClose={() => createUrlMutation.reset()}
      >
        <Alert
          severity={createUrlMutation.isSuccess ? "success" : "error"}
          onClose={() => createUrlMutation.reset()}
        >
          {createUrlMutation.isSuccess
            ? "Short URL created successfully!"
            : "Failed to create short URL. Please try again."}
        </Alert>
      </Snackbar>

      {/* Copied notification */}
      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={() => setShowCopied(false)}
      >
        <Alert severity="success">URL copied to clipboard!</Alert>
      </Snackbar>
    </>
  );
};

export default UrlCreationModal;
